import { Box, Separator, Spinner, VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import Link from "@pathwright/ux/src/components/link/Link"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { Formik } from "formik"
import {
  FeatureAction,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionQuery,
  useCreateContextualFeaturePermissionMutation,
  useUpdateContextualFeaturePermissionMutation
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

const FeaturePermissionsCoverPagesForm = () => {
  const { me } = usePathwrightContext()
  const isSuperuser = me?.permissions?.has_pathwright_staff_access

  const baseVariables = {
    context: null,
    featureKey: FeatureKey.LibraryCoverPages,
    featureAction: FeatureAction.CoverPagesView
  }

  const query = useContextualFeaturePermissionQuery({
    variables: baseVariables
  })

  const [createMutation] = useCreateContextualFeaturePermissionMutation()
  const [updateMutation] = useUpdateContextualFeaturePermissionMutation()
  const mutation = query.data?.contextualFeaturePermission
    ? updateMutation
    : createMutation

  if (query.loading) {
    return (
      <VStack py={4} justifyContent="center">
        <Spinner />
      </VStack>
    )
  }

  if (query.error) {
    return <Alert error={query.error} />
  }

  const cfp = query.data?.contextualFeaturePermission?.permission_in_context
  const isPreviewable = cfp
    ? [FeaturePermission.SchoolStaffOnly, FeaturePermission.Everyone].includes(
        cfp
      )
    : false
  const isLaunched = cfp === FeaturePermission.Everyone

  const initialValues = {
    previewable: isPreviewable,
    launched: isLaunched
  }

  return (
    <Formik
      initialValues={initialValues}
      // enableReinitialize allows the form to reset to new state after submission
      enableReinitialize
      onSubmit={async (values) => {
        let permissionInContext = FeaturePermission.Disabled

        if (values.launched) {
          permissionInContext = FeaturePermission.Everyone
        } else if (values.previewable) {
          permissionInContext = FeaturePermission.SchoolStaffOnly
        }

        await mutation({
          variables: {
            ...baseVariables,
            permissionInContext
          }
        })
        await query.refetch()
      }}
    >
      {(form) => {
        return (
          <Box
            css={{
              "& h3": {
                fontWeight: 400,
                margin: 0,
                marginBottom: "8px"
              },
              "& p": {
                color: "rgb(161, 161, 161)",
                margin: 0,
                fontSize: "0.9em"
              }
            }}
          >
            <Box
              as="form"
              minW="full"
              py={4}
              css={{
                "& .ToggleInput--disabled, & .ToggleInput--disabled *": {
                  cursor: "not-allowed"
                }
              }}
              >
                <h3>Enable preview</h3>
                <p>
                  Enable Admins & Library editors to optionally preview a Cover
                  page via a link from the current About Page.
              </p>
              <Tooltip
                title={
                  <Box>
                    When preview is enabled, Admins & Library Editors will see a
                    “Cover Page” button at the top right of each About Page.
                    Click it to view the Cover Page version. Then, update your
                    current text, images, and settings to maximize the new
                    layout.{" "}
                    <Link
                      display="inline"
                      whiteSpace="normal"
                      target="_blank"
                      color="brand.400"
                      textDecoration="underline"
                      to="https://help.pathwright.com/en/articles/432831-about-page-make-a-great-course-pitch"
                    >
                      Get tips for updating your pages here.
                    </Link>
                  </Box>
                }
              >
                <ToggleInput
                  labelWidth={300}
                  label="Preview with staff only"
                  value={form.values.previewable}
                  onChange={async (value: boolean) => {
                    await form.setFieldValue("previewable", value, false)
                    form.submitForm()
                  }}
                  disabled={!!isLaunched}
                  alignRight
                  style={{
                    width: "100%"
                  }}
                />
              </Tooltip>
              <p style={{ marginBottom: "12px" }}>
                Let admins preview Cover Pages without affecting what learners
                see.
              </p>
              <Separator borderBottomWidth={0} borderTopWidth="1px" w="full" />
              <Tooltip
                title={
                  <Box>
                    When toggled on, all About Pages will be replaced with Cover
                    Pages. This will not affect any of your data, and is a
                    reversible change.
                  </Box>
                }
              >
                <ToggleInput
                  labelWidth={300}
                  label="Launch Cover Pages"
                  value={form.values.launched}
                  // Allow SUs to disable Cover Pages launch
                  disabled={!isSuperuser && !!isLaunched}
                  onChange={async (value: boolean) => {
                    await form.setFieldValue("launched", value, false)
                    // If Cover Pages are launched, enable staff preview by default
                    if (value === true) {
                      await form.setFieldValue("previewable", true, false)
                    }
                    form.submitForm()
                  }}
                  alignRight
                  style={{
                    width: "100%"
                  }}
                />
              </Tooltip>
              <p>
                Replace your existing About Pages with Cover Pages. Your existing
                content is reused and can be reverted at any time.
              </p>
            </Box>
          </Box>
        )
      }}
    </Formik>
  )
}

export default FeaturePermissionsCoverPagesForm
